import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { scrollPageToTop } from '@/navigation/ScrollToTop';

/** The portal's tabs, in the order the tab bar shows them. */
export const EMPLOYEE_PORTAL_TABS = ['summary', 'objectives'] as const;

export type EmployeePortalTab = (typeof EMPLOYEE_PORTAL_TABS)[number];

const TAB_PARAM = 'tab';

/**
 * The open tab, read from and written to `?tab=` so a reload, a bookmark or a link from the host
 * reopens the same view. A missing, unknown or unavailable tab opens the first available one, and a
 * bookmark on a tab the company no longer offers therefore lands on another rather than on nothing.
 * When the company offers none, there is no open tab and the caller says so in its own words. The
 * address is replaced, not pushed: the back button leaves the portal instead of replaying every tab
 * visited. Other parameters, such as the Objectives tab's `?period=`, are kept. Like any other
 * screen, the tab opens at the top of the page.
 */
export const useEmployeePortalTab = (availableTabs: readonly EmployeePortalTab[]) => {
    const history = useHistory();
    const location = useLocation();

    const requestedTab = new URLSearchParams(location.search).get(TAB_PARAM);
    const activeTab: EmployeePortalTab | undefined =
        availableTabs.find((tab) => tab === requestedTab) ?? availableTabs[0];

    const openTab = useCallback(
        (tab: EmployeePortalTab) => {
            const params = new URLSearchParams(location.search);
            params.set(TAB_PARAM, tab);
            history.replace({ search: params.toString() });
            scrollPageToTop();
        },
        [history, location.search],
    );

    return { activeTab, openTab };
};
